import React, { useState } from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface IntegrationConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const IntegrationConfig: React.FC<IntegrationConfigProps> = ({ node, updateConfig }) => {
  const [headers, setHeaders] = useState<Array<{key: string, value: string}>>(
    node.data.config?.headers ? Object.entries(node.data.config.headers).map(([key, value]) => ({key, value: value as string})) : []
  );

  const addHeader = () => {
    const newHeaders = [...headers, { key: '', value: '' }];
    setHeaders(newHeaders);
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
    
    // Convert to object
    const headersObj = newHeaders.reduce((acc, h) => {
      if (h.key) acc[h.key] = h.value;
      return acc;
    }, {} as Record<string, string>);
    updateConfig('headers', headersObj);
  };

  const removeHeader = (index: number) => {
    const newHeaders = headers.filter((_, i) => i !== index);
    setHeaders(newHeaders);
    
    const headersObj = newHeaders.reduce((acc, h) => {
      if (h.key) acc[h.key] = h.value;
      return acc;
    }, {} as Record<string, string>);
    updateConfig('headers', headersObj);
  };

  return (
    <div className="space-y-4">
      <ConfigSection title="HTTP Kérés beállítások">
        <ConfigField
          label="HTTP Metódus"
          description="A kérés típusa"
        >
          <select
            value={node.data.config?.method || 'GET'}
            onChange={(e) => updateConfig('method', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </ConfigField>

        <ConfigField
          label="URL"
          description="A teljes API endpoint URL"
        >
          <input
            type="url"
            value={node.data.config?.url || ''}
            onChange={(e) => updateConfig('url', e.target.value)}
            placeholder="https://api.example.com/endpoint"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>

        {(node.data.config?.method === 'POST' || node.data.config?.method === 'PUT' || node.data.config?.method === 'PATCH') && (
          <ConfigField
            label="Request Body"
            description="JSON formátumú adatok (változók használhatók)"
          >
            <textarea
              value={node.data.config?.body || ''}
              onChange={(e) => updateConfig('body', e.target.value)}
              placeholder='{"key": "{{value}}"}'
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </ConfigField>
        )}
      </ConfigSection>

      <ConfigSection title="HTTP Headers">
        <div className="space-y-2">
          {headers.map((header, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={header.key}
                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                placeholder="Header név"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <input
                type="text"
                value={header.value}
                onChange={(e) => updateHeader(index, 'value', e.target.value)}
                placeholder="Érték"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <button
                onClick={() => removeHeader(index)}
                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={addHeader}
            className="w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
          >
            + Header hozzáadása
          </button>
        </div>
      </ConfigSection>

      <ConfigSection title="Beállítások">
        <ConfigField
          label="Timeout (másodperc)"
          description="Maximum várakozási idő"
        >
          <input
            type="number"
            value={node.data.config?.timeout || 30}
            onChange={(e) => updateConfig('timeout', parseInt(e.target.value))}
            min="1"
            max="300"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="followRedirects"
            checked={node.data.config?.followRedirects !== false}
            onChange={(e) => updateConfig('followRedirects', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="followRedirects" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Átirányítások követése
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="verifySsl"
            checked={node.data.config?.verifySsl !== false}
            onChange={(e) => updateConfig('verifySsl', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="verifySsl" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            SSL tanúsítvány ellenőrzése
          </label>
        </div>
      </ConfigSection>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>🔌 API Integráció:</strong>
          <br />• Bármilyen REST API hívható
          <br />• Változók használhatók: <code>{'{{variable}}'}</code>
          <br />• Válasz elérhető: <code>{'{{response}}'}</code>
          <br />• Státusz kód: <code>{'{{status_code}}'}</code>
        </div>
      </div>
    </div>
  );
};

export default IntegrationConfig;
